import { useAuthData } from 'components';
import { useState } from 'react';
import TagManager from 'react-gtm-module';
import { useMutation } from 'react-query';
import { ApiError } from '../../services/apiError';
import {
  lowerReserveReferred,
  lowerReserveAuction,
  LowerReserveRequestType,
  holdReserve,
  HoldReserveRequestType,
  ReserveReturnType,
  acceptBid,
  AcceptBidRequestType,
} from '../../services/listings';
import {
  AcceptBidModal,
  HoldReserveModal,
  ViewPaymentDetailsModal,
  ReferredInfoModal,
  isViewPaymentDetailsPayload,
  isAcceptBidPayload,
  isHoldReservePayload,
  isLowerReservePayload,
  ListingModalData,
  ListingModalType,
  ListingModalTypeEnum,
  LowerReserveModal,
  SetModalContent,
  isReferredInfoPayload,
} from './Modals';

/**
 * This will handle all interactions with the various listings modals.
 * Its props are based on the modal state
 * It will defined the API calls to make from actions taken inside the various modals.
 * Eg. onLowerReserve, onAcceptBid and so on.
 */
export function ListingsModal<T extends ListingModalType>({
  content,
  toggle,
  isOpen,
  isApiSuccess,
  setIsApiSuccess,
  setContent,
}: ListingsModalState<T>) {
  const { token } = useAuthData();

  /**
   * TODO: define necessary API calls here for each modal type
   */
  const lowerReserveReferredMutation = useMutation<
    ReserveReturnType,
    ApiError,
    LowerReserveRequestType
  >((lowerReserveRequest) => lowerReserveReferred(token, lowerReserveRequest), {
    onSuccess: () => {
      TagManager.dataLayer({
        dataLayer: {
          event: 'referredLowerReserveModal-onSubmit',
        },
      });
      setIsApiSuccess(true);
    },
    onError: () =>
      TagManager.dataLayer({
        dataLayer: {
          event: 'auctionLowerReserveModal-onError',
        },
      }),
  });

  const lowerReserveAuctionMutation = useMutation<
    ReserveReturnType,
    ApiError,
    LowerReserveRequestType
  >((lowerReserveRequest) => lowerReserveAuction(token, lowerReserveRequest), {
    onSuccess: () => {
      TagManager.dataLayer({
        dataLayer: {
          event: 'auctionLowerReserveModal-onSubmit',
        },
      });
      setIsApiSuccess(true);
    },
    onError: () =>
      TagManager.dataLayer({
        dataLayer: {
          event: 'auctionLowerReserveModal-onError',
        },
      }),
  });

  const holdReserveMutation = useMutation<
    ReserveReturnType,
    ApiError,
    HoldReserveRequestType
  >((holdReserveRequest) => holdReserve(token, holdReserveRequest), {
    onSuccess: () => setIsApiSuccess(true),
  });

  const acceptBidMutation = useMutation<
    ReserveReturnType,
    ApiError,
    AcceptBidRequestType
  >((acceptBidRequest) => acceptBid(token, acceptBidRequest), {
    onSuccess: () => {
      TagManager.dataLayer({
        dataLayer: {
          event: 'referredAcceptBidModal-acceptBid',
        },
      });
      setIsApiSuccess(true);
    },
  });

  if (isLowerReservePayload(content)) {
    const { bid, listingType, reserve, lotSku, remainingActionCount } =
      content.data;

    if (listingType === 'referred') {
      return (
        <LowerReserveModal
          bid={bid}
          isOpen={isOpen}
          listingType={listingType}
          reserve={reserve}
          lotSku={lotSku}
          remainingActionCount={remainingActionCount}
          apiError={lowerReserveReferredMutation?.error ?? undefined}
          isButtonLoading={lowerReserveReferredMutation.isLoading}
          isSuccess={isApiSuccess}
          onLowerReserve={(newReserve) => {
            lowerReserveReferredMutation.mutate({
              LotSku: lotSku,
              NewReservePrice: newReserve,
            });
          }}
          onClose={toggle}
        />
      );
    }

    return (
      <LowerReserveModal
        bid={bid}
        isOpen={isOpen}
        listingType={listingType}
        reserve={reserve}
        lotSku={lotSku}
        remainingActionCount={remainingActionCount}
        apiError={lowerReserveAuctionMutation?.error ?? undefined}
        isButtonLoading={lowerReserveAuctionMutation.isLoading}
        isSuccess={isApiSuccess}
        onLowerReserve={(newReserve) => {
          lowerReserveAuctionMutation.mutate({
            LotSku: lotSku,
            NewReservePrice: newReserve,
          });
        }}
        onClose={toggle}
      />
    );
  }

  if (isAcceptBidPayload(content)) {
    return (
      <AcceptBidModal
        isOpen={isOpen}
        currentBidText={content.data.currentBidText}
        lotSku={content.data.lotSku}
        isButtonLoading={acceptBidMutation.isLoading}
        isSuccess={isApiSuccess}
        onAcceptBid={() => {
          acceptBidMutation.mutate({
            LotSku: content.data.lotSku,
          });
        }}
        onClose={() => {
          TagManager.dataLayer({
            dataLayer: {
              event: 'referredAcceptBidModal-cancel',
            },
          });
          toggle();
        }}
      />
    );
  }

  if (isHoldReservePayload(content)) {
    return (
      <HoldReserveModal
        isOpen={isOpen}
        isButtonLoading={holdReserveMutation.isLoading}
        isSuccess={isApiSuccess}
        onAcceptBidLink={() =>
          content.data.secondaryData &&
          setContent({
            type: ListingModalTypeEnum.AcceptBid,
            data: content.data.secondaryData,
          })
        }
        onHoldReserve={() => {
          holdReserveMutation.mutate({
            LotSku: content.data.lotSku,
          });
        }}
        onClose={toggle}
      />
    );
  }
  if (isViewPaymentDetailsPayload(content)) {
    return (
      <ViewPaymentDetailsModal
        isOpen={isOpen}
        onClose={toggle}
        lotSku={content.data.lotSku}
      />
    );
  }
  if (isReferredInfoPayload(content)) {
    return <ReferredInfoModal isOpen={isOpen} onClose={toggle} />;
  }
  return null;
}

export type ListingsModalState<T extends ListingModalType> = {
  content: { data: ListingModalData<T>; type: T };
  isOpen: boolean;
  isApiSuccess: boolean;
  setIsApiSuccess: (value: boolean) => void;
  toggle: () => void;
  setContent: SetModalContent;
};

type UseListingsModalInitial<
  T extends ListingModalType = ListingModalTypeEnum.HoldReserve
> = {
  type: T;
  data: ListingModalData<T>;
};
export const useListingsModal = (
  initial: UseListingsModalInitial = {
    type: ListingModalTypeEnum.HoldReserve,
    data: { lotSku: '' },
  }
) => {
  const [modalType, setModalType] = useState<ListingModalType>(initial.type);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [modalData, setModalData] = useState<
    ListingModalData<typeof modalType>
  >(initial.data);
  const [isApiSuccess, setIsApiSuccess] = useState(false);

  const setModalContent: SetModalContent = ({ type, data }) => {
    setModalType(type);
    setModalData(data);
    setIsModalOpen(true);
    setIsApiSuccess(false);
  };

  const toggleModal = () => {
    setModalType(initial.type);
    setModalData(initial.data);
    setIsApiSuccess(false);
    setIsModalOpen(!isModalOpen);
  };

  return {
    content: { data: modalData, type: modalType },
    isOpen: isModalOpen,
    isApiSuccess: isApiSuccess,
    setIsApiSuccess: setIsApiSuccess,
    toggle: toggleModal,
    setContent: setModalContent,
  };
};
